import React from "react";
import { useAppSelector } from "@/store/hook";
import { ApiCardType, DashboardCard } from "@/store/slices/dashboardSlice";

export type DashboardCardWithPair = Omit<DashboardCard, "type"> & {
  type: ApiCardType | "PCP_HEALTH_PLAN_PAIR";
};

interface PreferredLayoutProps {
  cardsWithMdLayout: DashboardCardWithPair[];
  layoutType: string;
  blinkingCard: ApiCardType | "PCP_HEALTH_PLAN_PAIR" | null;
  setCardRef: (cardType: ApiCardType | "PCP_HEALTH_PLAN_PAIR", element: HTMLDivElement | null) => void;
  cardComponents: Record<ApiCardType, React.ComponentType<{ shouldBlink?: boolean }>>;
  layoutGrid: Record<string, string>;
  layoutColumnCount: Record<string, number>;
}

const PreferredLayout: React.FC<PreferredLayoutProps> = ({
  cardsWithMdLayout,
  layoutType,
  blinkingCard,
  setCardRef,
  cardComponents,
  layoutGrid,
  layoutColumnCount,
}) => {
  const columnCount = layoutColumnCount[layoutType] || 3;
  const gridTemplate = layoutGrid[layoutType] || layoutGrid["grid"];

  // Raw cards give us the individual layoutPosition for PCP and HEALTH_PLAN
  // before Dashboard/index.tsx merges them into PCP_HEALTH_PLAN_PAIR.
  const rawCards = useAppSelector((state) => state.dashboard.cards);

  // Fixed cards are identified by layoutPosition === "fixed" from the API.
  // Sort by order so the first fixed card (Demographics) goes to col 0,
  // and the second (Care Management) goes to col 1 in multi-column layouts.
  const fixedCardsSorted = rawCards
    .filter((c) => c.layoutPosition === "fixed")
    .sort((a, b) => a.order - b.order);
  const fixedCardOne = fixedCardsSorted[0];
  const fixedCardTwo = fixedCardsSorted[1];

  // Build per-column card lists strictly from saved layoutPosition.
  // PCP_HEALTH_PLAN_PAIR is broken apart so each card lands in its own
  // user-assigned column (e.g. PCP left, Health Plan right in a 50-50 split).
  const columns: DashboardCardWithPair[][] = Array.from(
    { length: columnCount },
    () => []
  );

  cardsWithMdLayout
    .filter(card => card.layoutPosition !== "fixed")
    .forEach((card) => {
      if (card.type === "PCP_HEALTH_PLAN_PAIR") {
        // Preferred layout: break the forced pairing and respect individual positions.
        const rawPcp = rawCards.find(c => c.type === "PCP");
        const rawHealthPlan = rawCards.find(c => c.type === "HEALTH_PLAN");

        if (rawPcp) {
          const colIndex = parseInt(rawPcp.layoutPosition || "0") - 1;
          if (colIndex >= 0 && colIndex < columnCount) {
            columns[colIndex].push({
              ...card,
              type: "PCP",
              id: rawPcp.id,
              layoutPosition: rawPcp.layoutPosition,
              order: rawPcp.order,
            });
          }
        }

        if (rawHealthPlan) {
          const colIndex = parseInt(rawHealthPlan.layoutPosition || "0") - 1;
          if (colIndex >= 0 && colIndex < columnCount) {
            columns[colIndex].push({
              ...card,
              type: "HEALTH_PLAN",
              id: rawHealthPlan.id,
              layoutPosition: rawHealthPlan.layoutPosition,
              order: rawHealthPlan.order,
            });
          }
        }
      } else {
        // All other cards: place at their saved column position.
        const colIndex = parseInt(card.layoutPosition || "0") - 1;
        if (colIndex >= 0 && colIndex < columnCount) {
          columns[colIndex].push(card);
        }
        // Cards with invalid / out-of-range positions are silently dropped.
      }
    });

  // Sort each column by the card's global order so that splitting PCP_HEALTH_PLAN_PAIR
  // never places PCP ahead of cards (e.g. Referrals) that should precede it.
  columns.forEach((col) => col.sort((a, b) => a.order - b.order));

  return (
    <div className="flex flex-col gap-[20px]">

      {/* Single unified grid — each column owns its fixed card(s) + dynamic cards.
          Columns without a fixed card (e.g. col 3 in a 3-col layout) start their
          dynamic cards from the very top, aligned with Demographics in col 1. */}
      <div
        className="grid gap-[20px] items-start"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {Array.from({ length: columnCount }, (_, index) => {
          const colCards = columns[index] ?? [];

          return (
            <div key={`col-${index + 1}`} className="flex flex-col gap-[20px] min-w-0">
              {/* Fixed cards driven by layoutPosition === "fixed" from API.
                  First fixed card always renders at top of col 0.
                  Second fixed card renders in col 0 for single-column, col 1 for multi-column. */}
              {index === 0 && fixedCardOne && (() => {
                const FixedCard = cardComponents[fixedCardOne.type];
                return FixedCard ? (
                  <div ref={(el) => setCardRef(fixedCardOne.type, el)}>
                    <FixedCard shouldBlink={blinkingCard === fixedCardOne.type} />
                  </div>
                ) : null;
              })()}
              {((columnCount === 1 && index === 0) || (columnCount > 1 && index === 1)) && fixedCardTwo && (() => {
                const FixedCard = cardComponents[fixedCardTwo.type];
                return FixedCard ? (
                  <div ref={(el) => setCardRef(fixedCardTwo.type, el)}>
                    <FixedCard shouldBlink={blinkingCard === fixedCardTwo.type} />
                  </div>
                ) : null;
              })()}

              {/* Dynamic cards assigned by the user to this column.
                  PCP and HEALTH_PLAN are treated as independent cards here — no
                  forced pairing — so a 50-50 layout renders them side-by-side. */}
              {colCards.map((card) => {
                const CardComponent = cardComponents[card.type as ApiCardType];
                const shouldBlink = blinkingCard === card.type;

                return (
                  <div key={card.id} ref={(el) => setCardRef(card.type as ApiCardType, el)}>
                    {CardComponent && <CardComponent shouldBlink={shouldBlink} />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default PreferredLayout;
